import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, Star, Gavel, WifiHigh } from 'lucide-react';
import i18n from '../../../../i18n';
import { getClientsNew } from '../../../../models/organization';
import '../../../../styles/components/sellers-list.scss';

const PAGE_SIZE_OPTIONS = [12, 24, 48];

export class SellersList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            sellers: [],
            loading: false,
            viewMode: 'grid',       // 'grid' | 'list'
            orderBy: 'name',
            searchQuery: '',
            currentPage: 1,
            pageSize: 12,
            totalCount: 0,
            showOrderDropdown: false,
            showPageSizeDropdown: false,
        };
        this.orderRef = React.createRef();
        this.pageSizeRef = React.createRef();
        this.searchTimeout = null;
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        document.addEventListener('mousedown', this.handleClickOutside);
        this.loadSellers();
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
    }

    handleClickOutside(e) {
        if (this.orderRef.current && !this.orderRef.current.contains(e.target)) {
            this.setState({ showOrderDropdown: false });
        }
        if (this.pageSizeRef.current && !this.pageSizeRef.current.contains(e.target)) {
            this.setState({ showPageSizeDropdown: false });
        }
    }

    async loadSellers() {
        this.setState({ loading: true });
        try {
            const { currentPage, pageSize, orderBy, searchQuery } = this.state;
            const params = `page=${currentPage}&limit=${pageSize}&order=${orderBy}&search=${searchQuery}`;
            const response = await getClientsNew(params);
            this.setState({
                sellers: response?.data || [],
                totalCount: response?.total || 0,
                loading: false,
            });
        } catch (e) {
            this.setState({ loading: false });
        }
    }

    setViewMode = (mode) => this.setState({ viewMode: mode });

    setOrderBy = (value) => {
        this.setState({ orderBy: value, showOrderDropdown: false, currentPage: 1 }, () => this.loadSellers());
    };

    handleSearch = (e) => {
        const val = e.target.value;
        this.setState({ searchQuery: val });
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            this.setState({ currentPage: 1 }, () => this.loadSellers());
        }, 400);
    };

    setPageSize = (size) => {
        this.setState({ pageSize: size, currentPage: 1, showPageSizeDropdown: false }, () => this.loadSellers());
    };

    goToPage = (page) => {
        const totalPages = Math.ceil(this.state.totalCount / this.state.pageSize);
        if (page < 1 || page > totalPages) return;
        this.setState({ currentPage: page }, () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.loadSellers();
        });
    };

    renderStars(rating = 0) {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <Star
                    key={i}
                    size={14}
                    className={`sl-star ${i <= rating ? 'sl-star--filled' : ''}`}
                />
            );
        }
        return stars;
    }

    renderPagination() {
        const { currentPage, pageSize, totalCount } = this.state;
        const totalPages = Math.ceil(totalCount / pageSize);
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

        for (let i = start; i <= end; i++) pages.push(i);

        return (
            <div className="sl-pagination">
                <button
                    className="sl-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => this.goToPage(1)}
                >
                    <ChevronsLeft size={16} />
                </button>
                <button
                    className="sl-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => this.goToPage(currentPage - 1)}
                >
                    <ChevronLeft size={16} />
                </button>

                {pages.map(p => (
                    <button
                        key={p}
                        className={`sl-page-btn sl-page-btn--num ${p === currentPage ? 'sl-page-btn--active' : ''}`}
                        onClick={() => this.goToPage(p)}
                    >
                        {p}
                    </button>
                ))}

                <button
                    className="sl-page-btn sl-page-btn--next"
                    disabled={currentPage === totalPages}
                    onClick={() => this.goToPage(currentPage + 1)}
                >
                    <ChevronRight size={16} />
                </button>
                <button
                    className="sl-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => this.goToPage(totalPages)}
                >
                    <ChevronsRight size={16} />
                </button>
            </div>
        );
    }

    render() {
        const {
            sellers, loading, viewMode, orderBy, searchQuery,
            currentPage, pageSize, totalCount,
            showOrderDropdown, showPageSizeDropdown,
        } = this.state;

        const totalPages = Math.ceil(totalCount / pageSize);
        const rangeStart = (currentPage - 1) * pageSize + 1;
        const rangeEnd = Math.min(currentPage * pageSize, totalCount);

        const ORDER_OPTIONS = [
            { value: 'name', label: i18n.t('name') || 'Name' },
            { value: 'auctions', label: i18n.t('auctions') || 'Auctions' },
            // { value: 'rating', label: i18n.t('rating') || 'Rating' },
        ];
        const activeOrderLabel = ORDER_OPTIONS.find(o => o.value === orderBy)?.label || 'Order by';

        return (
            <div className="sl-page">
                <div className="container">

                    {/* ── Title ── */}
                    <h1 className="sl-title">
                        {i18n.t('sellers_list') || 'Sellers List'}
                    </h1>

                    {/* ── Toolbar ── */}
                    <div className="sl-toolbar">
                        <div className="sl-toolbar__left">

                            {/* View toggle */}
                            {/* <div className="af-view-toggle">
                                <button
                                    className={`af-view-toggle__btn ${viewMode === 'grid' ? 'af-view-toggle__btn--active' : ''}`}
                                    onClick={() => this.setViewMode('grid')}
                                >
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <rect x="1" y="1" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                                        <rect x="10" y="1" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                                        <rect x="1" y="10" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                                        <rect x="10" y="10" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                                    </svg>
                                </button>
                                <button
                                    className={`af-view-toggle__btn ${viewMode === 'list' ? 'af-view-toggle__btn--active' : ''}`}
                                    onClick={() => this.setViewMode('list')}
                                >
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <rect x="1" y="2" width="16" height="3" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                        <rect x="1" y="7.5" width="16" height="3" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                        <rect x="1" y="13" width="16" height="3" rx="1" stroke="currentColor" strokeWidth="1.5" />
                                    </svg>
                                </button>
                            </div> */}

                            {/* Order by */}
                            <div className="af-dropdown" ref={this.orderRef}>
                                <button
                                    className="af-dropdown__trigger"
                                    onClick={() => this.setState(s => ({ showOrderDropdown: !s.showOrderDropdown }))}
                                >
                                    <span>{i18n.t('order_by') || 'Order by'}</span>
                                    <ChevronDown
                                        size={14}
                                        className={`af-dropdown__chevron ${showOrderDropdown ? 'af-dropdown__chevron--open' : ''}`}
                                    />
                                </button>
                                {showOrderDropdown && (
                                    <div className="af-dropdown__menu af-dropdown__menu--sort">
                                        {ORDER_OPTIONS.map(opt => (
                                            <button
                                                key={opt.value}
                                                className={`af-dropdown__item ${orderBy === opt.value ? 'af-dropdown__item--active' : ''}`}
                                                onClick={() => this.setOrderBy(opt.value)}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Search */}
                            <div className="sl-search">
                                <input
                                    type="text"
                                    className="sl-search__input"
                                    placeholder={i18n.t('search') || 'Search'}
                                    value={searchQuery}
                                    onChange={this.handleSearch}
                                />
                                <Search size={15} className="sl-search__icon" />
                            </div>
                        </div>

                        <div className="sl-toolbar__right">
                            {totalCount > 0 && (
                                <span className="sl-range-label">
                                    {rangeStart} - {rangeEnd} {i18n.t('of') || 'of'} {totalCount}
                                </span>
                            )}

                            {/* Page size */}
                            <div className="af-dropdown" ref={this.pageSizeRef}>
                                <button
                                    className="af-dropdown__trigger"
                                    onClick={() => this.setState(s => ({ showPageSizeDropdown: !s.showPageSizeDropdown }))}
                                >
                                    <span>{pageSize}</span>
                                    <ChevronDown
                                        size={14}
                                        className={`af-dropdown__chevron ${showPageSizeDropdown ? 'af-dropdown__chevron--open' : ''}`}
                                    />
                                </button>
                                {showPageSizeDropdown && (
                                    <div className="af-dropdown__menu af-dropdown__menu--sort">
                                        {PAGE_SIZE_OPTIONS.map(size => (
                                            <button
                                                key={size}
                                                className={`af-dropdown__item ${pageSize === size ? 'af-dropdown__item--active' : ''}`}
                                                onClick={() => this.setPageSize(size)}
                                            >
                                                {size}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Loading ── */}
                    {loading && (
                        <div className="sl-loading">
                            <div className="sl-spinner" />
                            <span>{i18n.t('loading') || 'Loading...'}</span>
                        </div>
                    )}

                    {/* ── Empty ── */}
                    {!loading && sellers.length === 0 && (
                        <div className="sl-empty">
                            <Gavel size={40} />
                            <p>{i18n.t('no_sellers') || 'No sellers found'}</p>
                        </div>
                    )}

                    {/* ── Grid / List ── */}
                    {!loading && sellers.length > 0 && (
                        <div className={`sl-grid ${viewMode === 'list' ? 'sl-grid--list' : ''}`}>
                            {sellers.map((seller, i) => (
                                <Link
                                    to={`/seller/${seller.id}`}
                                    className="sl-card"
                                    key={seller.id || i}
                                >
                                    <div className="sl-card__logo-wrap">
                                        <img
                                            src={seller.logo || seller.organization_image_url || (process.env.PUBLIC_URL + '/assets/images/placeholder.jpg')}
                                            alt={seller.name || ''}
                                            className="sl-card__logo"
                                        />
                                    </div>
                                    <div className="sl-card__info">
                                        <h3 className="sl-card__name">
                                            {seller.name || seller.organization_name || '—'}
                                        </h3>
                                        <div className="sl-card__stats">
                                            <span className="sl-card__stat">
                                                <Gavel size={13} />
                                                {i18n.t('number_of_auctions')}  &nbsp;
                                                {seller.auction_count ?? seller.total_auctions ?? 0}
                                            </span>
                                            <span className="sl-card__stat">
                                                &amp;  <WifiHigh size={13} />
                                                     {i18n.t('live_auctions')} {seller.active_auctions ?? 0} &nbsp;
                                            </span>
                                            {/* <span className="sl-card__stat">
                                                <Clipboard size={13} /> {seller.posts ?? 0}
                                            </span> */}
                                        </div>
                                        {/* <div className="sl-card__rating">
                                            {this.renderStars(seller.rating ?? 0)}
                                        </div> */}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}

                    {/* ── Pagination ── */}
                    {!loading && this.renderPagination()}
                </div>
            </div>
        );
    }
}

export default SellersList;